import { Ref } from 'react';
import * as Headless from '@headlessui/react';
import clsx from 'clsx';

/**
 * A styled textarea component based on Headless UI.
 */
export function Textarea({
  className,
  resizable = true,
  ref,
  ...props
}: {
  /** Optional CSS class name. */
  className?: string;
  /** Whether the textarea is resizable. */
  resizable?: boolean;
  /** React ref for the underlying textarea element. */
  ref?: Ref<HTMLTextAreaElement>;
} & Omit<Headless.TextareaProps, 'as' | 'className'>) {
  return (
    <span
      className={clsx([
        className,
        // Basic layout
        'relative block w-full',
        // Background color + shadow applied to inset pseudo element, so shadow
        // blends with border in light mode
        'before:absolute before:inset-px before:rounded-[calc(var(--radius-lg)-1px)] before:bg-white before:shadow-sm',
        // Background color is moved to control and shadow is removed in
        // dark mode so hide `before` pseudo
        'dark:before:hidden',
        // Focus ring
        'sm:focus-within:after:ring-primary-500 after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-inset after:ring-transparent sm:focus-within:after:ring-2',
        // Disabled state
        'has-data-disabled:opacity-50 has-data-disabled:before:bg-gray-950/5 has-data-disabled:before:shadow-none',
      ])}
      data-slot="control"
    >
      <Headless.Textarea
        ref={ref}
        {...props}
        className={clsx([
          // Basic layout
          'relative block h-full w-full appearance-none rounded-lg px-[calc(--spacing(3.5)-1px)] py-[calc(--spacing(2.5)-1px)] sm:px-[calc(--spacing(3)-1px)] sm:py-[calc(--spacing(1.5)-1px)]',
          // Typography
          'text-base/6 text-gray-950 placeholder:text-gray-500 dark:text-white sm:text-sm/6',
          // Border
          'data-hover:border-gray-950/20 dark:data-hover:border-white/20 border border-gray-950/10 dark:border-white/10',
          // Background color
          'bg-transparent dark:bg-white/5',
          // Hide default focus styles
          'focus:outline-hidden',
          // Invalid state
          'data-invalid:border-red-500 data-invalid:data-hover:border-red-500 dark:data-invalid:border-red-600 dark:data-invalid:data-hover:border-red-600',
          // Disabled state
          'dark:disabled:border-white/15 dark:data-hover:disabled:border-white/15 disabled:border-gray-950/20 dark:disabled:bg-white/[2.5%]',
          // Resizable
          resizable ? 'resize-y' : 'resize-none',
        ])}
      />
    </span>
  );
}
